import { Grid } from '@material-ui/core'
import CircularProgress from '@material-ui/core/CircularProgress'
import { makeStyles } from '@material-ui/core/styles'
import Alert from '@material-ui/lab/Alert'
import { startCase } from 'lodash'
import { useEffect, useState } from 'react'
import { ExternalLink } from 'shared/components/ExternalLink/ExternalLink'
import { Text } from 'shared/components/Text/Text'
import {
  getHoursFromDurationInHours,
  getMinutesFromDurationInHours,
  getTimeDifferenceFromNowInMinutes,
} from 'shared/helpers/formatters'
import { lightGray1, lightgreye3, midGray } from 'shared/theme/colors'

import { captureExceptionIfProduction } from 'helpers/utils'
import { getUserELDAvailableTime, getUserELDConnectednessErrors } from '../../api/compliance'
import { CYCLE_LIMIT, DRIVING_BREAK, DRIVING_LIMIT, ON_DUTY_LIMIT } from '../../helpers/constants'
import { AvailableTime, Clock, ELDConnectionError } from '../../models/compliance'
import { ProductSubscriptionBundleName } from '../../models/subscription'
import { User } from '../../models/user'
import { SimpleCalloutField } from '../Fields'

const useStyles = makeStyles({
  alert: {
    width: '100%',
    '& .MuiAlert-message': {
      width: '100%',
    },
    marginBottom: '40px',
  },
})

const DutyStatusBadge = (props: { record: AvailableTime | null }) => {
  const { record } = props

  return record?.duty_status ? (
    <SimpleCalloutField
      styleProps={{
        style: {
          color: midGray,
          backgroundColor: lightgreye3,
          fontSize: 'medium',
          borderRadius: '3px',
        },
      }}
      label={startCase(record?.duty_status)}
      labelVariant="body2"
    />
  ) : null
}

const DisplayAvailableTime = (props: { availableTimeInMinutes: number; fieldName: string }) => {
  const { availableTimeInMinutes, fieldName } = props
  const hours = getHoursFromDurationInHours(availableTimeInMinutes)
  const minutes = getMinutesFromDurationInHours(availableTimeInMinutes)
  return (
    <Grid item container direction="column" xs={3}>
      <Text variant="paragraph2" fontColor={lightGray1} mt={0.5}>
        {fieldName}
      </Text>
      <Text variant="paragraph1">
        {hours < 10 ? '0' : ''}
        {hours}:{minutes < 10 ? '0' : ''}
        {minutes}
      </Text>
    </Grid>
  )
}

const getAvailableTimeForPolicyClock = (clocks: Clock[], policy: string) => {
  const clock: undefined | Clock = clocks.find((clock: Clock) => clock.rule.policy === policy)
  const availableTimeStr = clock?.time_until_rest
  if (availableTimeStr) {
    return parseFloat(availableTimeStr)
  }
  return null
}

export const ELDHOSClocks = (props: { record: User }) => {
  const { record } = props
  const [availableTime, setAvailableTime] = useState<null | AvailableTime>(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)

  useEffect(() => {
    setIsLoading(true)
    getUserELDAvailableTime(record.id)
      .then((response) => {
        setIsLoading(false)
        setAvailableTime(response.data)
      })
      .catch((error) => {
        setIsLoading(false)
        setAvailableTime(null)
        if (error?.response?.status !== 404) {
          captureExceptionIfProduction(error)
        }
      })
  }, [record, setAvailableTime])

  const breakTime = availableTime && getAvailableTimeForPolicyClock(availableTime.clocks, DRIVING_BREAK)
  const drivingLimitTime =
    availableTime && getAvailableTimeForPolicyClock(availableTime.clocks, DRIVING_LIMIT)
  const onDutyLimitTime = availableTime && getAvailableTimeForPolicyClock(availableTime.clocks, ON_DUTY_LIMIT)
  const cycleLimitTime = availableTime && getAvailableTimeForPolicyClock(availableTime.clocks, CYCLE_LIMIT)

  return (
    <>
      <Grid
        container
        item
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        xs={!!availableTime && !isLoading}
      >
        <Text variant="uppercaseLabel2" mt={0.5}>
          Hours of Service
        </Text>
        {availableTime && <DutyStatusBadge record={availableTime} />}
      </Grid>
      {isLoading && <CircularProgress color="inherit" size={20} />}
      {availableTime && !isLoading && (
        <>
          <Grid container item direction="column" justifyContent="space-between" xs>
            <Grid container item direction="row">
              {breakTime != null && (
                <DisplayAvailableTime fieldName="Until Break" availableTimeInMinutes={breakTime} />
              )}
              {drivingLimitTime != null && (
                <DisplayAvailableTime fieldName="Drive" availableTimeInMinutes={drivingLimitTime} />
              )}
              {onDutyLimitTime && (
                <DisplayAvailableTime fieldName="Shift" availableTimeInMinutes={onDutyLimitTime} />
              )}
              {cycleLimitTime && (
                <DisplayAvailableTime fieldName="Cycle" availableTimeInMinutes={cycleLimitTime} />
              )}
            </Grid>
          </Grid>
          <Grid container item alignItems="flex-end" xs>
            <Text variant="paragraph3" fontColor={lightGray1}>
              Updated {getTimeDifferenceFromNowInMinutes(availableTime.retrieved_at)} minutes ago
            </Text>
          </Grid>
        </>
      )}
      {!availableTime && !isLoading && (
        <Grid container item>
          <Text variant="label2" fontColor={lightGray1} mt={0.5}>
            NOT GETTING INFORMATION
          </Text>
        </Grid>
      )}
    </>
  )
}

export const ELDConnectionErrors = (props: { record: User }) => {
  const { record } = props
  const [eldConnectedErrors, setELDConnectedErrors] = useState<ELDConnectionError[]>([])
  const classes = useStyles()

  useEffect(() => {
    if (
      record &&
      record.id &&
      record.is_team_driver &&
      record.team?.product_subscription_name === ProductSubscriptionBundleName.PREMIUM
    ) {
      getUserELDConnectednessErrors(record.id)
        .then((response) => {
          const data = response.data
          setELDConnectedErrors(data['errors'])
        })
        .catch((error) => {
          setELDConnectedErrors([])
          if (![400, 404].includes(error?.response?.status)) {
            captureExceptionIfProduction(error)
          }
        })
    }
  }, [record])

  return eldConnectedErrors && eldConnectedErrors.length > 0 ? (
    <Grid container direction="row">
      <Alert severity="error" className={classes.alert}>
        <Text variant="header3" style={{ marginBottom: '10px' }}>
          {eldConnectedErrors.length} ELD issue{eldConnectedErrors.length > 1 ? 's' : ''}
        </Text>
        <Grid container direction="column" item spacing={1} xs={12}>
          {eldConnectedErrors.map((error) => (
            <Grid
              container
              item
              direction="row"
              justifyContent="flex-start"
              alignContent="center"
              spacing={1}
              xs={12}
            >
              <Grid container item xs={4}>
                <Text variant="paragraph1">&#183;&nbsp;&nbsp;&nbsp;{error.message}</Text>
              </Grid>
              <Grid container item xs={4}>
                <ExternalLink href={error.cta_url} target={'_blank'}>
                  {error.cta}
                </ExternalLink>
              </Grid>
            </Grid>
          ))}
        </Grid>
      </Alert>
    </Grid>
  ) : null
}
